import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, ExternalLink, KeyRound, Trash2, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PROVIDER_META, PROVIDER_ORDER } from "./providerMeta";

export const AiConnections = () => {
  const [rows, setRows] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try { setRows((await api.get("/ai/keys")).data); } catch { /* handled by auth */ }
  };
  useEffect(() => { load(); }, []);

  const save = async (provider) => {
    const api_key = (drafts[provider] || "").trim();
    if (!api_key) return;
    setBusy(provider);
    try {
      await api.post("/ai/keys", { provider, api_key });
      toast.success(`${PROVIDER_META[provider].name} connected`);
      setDrafts((d) => ({ ...d, [provider]: "" }));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not validate that key");
    } finally { setBusy(null); }
  };

  const remove = async (provider) => {
    setBusy(provider);
    try { await api.delete(`/ai/keys/${provider}`); toast.success("Disconnected"); load(); }
    catch { toast.error("Could not disconnect"); }
    finally { setBusy(null); }
  };

  const byProvider = Object.fromEntries(rows.map((r) => [r.provider, r]));

  return (
    <div className="space-y-4" data-testid="ai-connections">
      {PROVIDER_ORDER.map((p) => {
        const meta = PROVIDER_META[p];
        const row = byProvider[p] || {};
        return (
          <div key={p} className="bg-card border border-border rounded-lg p-5" data-testid={`ai-provider-${p}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-serif text-xl text-foreground">{meta.name}</h3>
                  <span className="text-xs text-muted-foreground">· {meta.vendor}</span>
                  {row.connected && (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-accent" data-testid={`ai-connected-${p}`}>
                      <CheckCircle2 className="w-3.5 h-3.5" /> Connected ••••{row.key_last4}
                    </span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mt-1 max-w-lg">{meta.blurb}</p>
              </div>
              <a href={meta.getKeyUrl} target="_blank" rel="noreferrer noopener"
                className="text-xs text-primary hover:underline inline-flex items-center gap-1 shrink-0">
                <KeyRound className="w-3.5 h-3.5" /> Get a key <ExternalLink className="w-3 h-3" />
              </a>
            </div>
            <div className="flex items-center gap-2 mt-3">
              <Input
                type="password"
                autoComplete="off"
                value={drafts[p] || ""}
                onChange={(e) => setDrafts((d) => ({ ...d, [p]: e.target.value }))}
                placeholder={row.connected ? "Paste a new key to replace" : meta.placeholder}
                data-testid={`ai-key-input-${p}`}
              />
              <Button onClick={() => save(p)} disabled={busy === p || !(drafts[p] || "").trim()}
                className="bg-primary hover:bg-primary/90 shrink-0" data-testid={`ai-save-${p}`}>
                {busy === p ? <Loader2 className="w-4 h-4 animate-spin" /> : (row.connected ? "Replace" : "Connect")}
              </Button>
              {row.connected && (
                <Button variant="outline" size="icon" onClick={() => remove(p)} disabled={busy === p}
                  className="shrink-0" data-testid={`ai-remove-${p}`}>
                  <Trash2 className="w-4 h-4 text-muted-foreground" />
                </Button>
              )}
            </div>
          </div>
        );
      })}
      <p className="text-xs text-muted-foreground">
        Your keys are encrypted and stored only on the server — never shown to anyone else, and never sent to the browser again.
        Usage is billed by each provider to your own account.
      </p>
    </div>
  );
};
